import { useCallback, useEffect, useState } from "react";
import {
  approveArticleComment,
  deleteArticleComment,
  fetchAdminArticles,
  fetchArticleComments,
  generateArticleComments,
  patchAdminArticle,
  patchArticleComment,
  rejectArticleComment,
  upsertAdminArticle,
  type GenerateArticleCommentsResult,
  type AdminArticle,
  type AdminArticleComment,
} from "@/api/admin";

type Props = {
  pendingCount: number;
  onPendingCountChange: (n: number) => void;
};

export function AdminArticleSocial({ pendingCount, onPendingCountChange }: Props) {
  const [articles, setArticles] = useState<AdminArticle[]>([]);
  const [comments, setComments] = useState<AdminArticleComment[]>([]);
  const [commentStatus, setCommentStatus] = useState("pending");
  const [filterByArticle, setFilterByArticle] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [genCount, setGenCount] = useState("3");
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState<GenerateArticleCommentsResult | null>(null);
  const [ragSaveInfo, setRagSaveInfo] = useState<string | null>(null);

  const [newSlug, setNewSlug] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newMarkdown, setNewMarkdown] = useState("");
  const [fetchFromUrl, setFetchFromUrl] = useState(true);

  const [editViews, setEditViews] = useState("");
  const [editLikes, setEditLikes] = useState("");
  const [editEnabled, setEditEnabled] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAuthor, setEditAuthor] = useState("");
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");

  const reloadArticles = useCallback(async () => {
    const data = await fetchAdminArticles();
    setArticles(data.articles);
    const pending = data.articles.reduce((s, a) => s + a.comments_pending, 0);
    onPendingCountChange(pending);
  }, [onPendingCountChange]);

  const reloadComments = useCallback(async () => {
    const data = await fetchArticleComments({
      status: commentStatus,
      slug: filterByArticle && selectedSlug ? selectedSlug : undefined,
      limit: 100,
    });
    setComments(data.comments);
  }, [commentStatus, filterByArticle, selectedSlug]);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([reloadArticles(), reloadComments()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [reloadArticles, reloadComments]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  const selected = articles.find((a) => a.slug === selectedSlug) ?? null;

  useEffect(() => {
    if (!selected) {
      setEditViews("");
      setEditLikes("");
      setEditEnabled(true);
      return;
    }
    setEditViews(String(selected.views_count));
    setEditLikes(String(selected.likes_count));
    setEditEnabled(selected.social_enabled);
  }, [selected]);

  const onApprove = async (id: number) => {
    setBusyId(id);
    try {
      await approveArticleComment(id);
      await reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (id: number) => {
    setBusyId(id);
    try {
      await rejectArticleComment(id);
      await reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (c: AdminArticleComment) => {
    setEditingId(c.id);
    setEditAuthor(c.author_display);
    setEditQuestion(c.question ?? "");
    setEditAnswer(c.answer);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAuthor("");
    setEditQuestion("");
    setEditAnswer("");
  };

  const onSaveComment = async (id: number) => {
    const answer = editAnswer.trim();
    if (answer.length < 3) {
      setError("Ответ — не менее 3 символов");
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await patchArticleComment(id, {
        author_display: editAuthor.trim() || "Гость",
        question: editQuestion.trim() || null,
        answer,
      });
      cancelEdit();
      await reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (c: AdminArticleComment) => {
    const label =
      c.status === "approved" ?
        "Удалить комментарий? Он сразу пропадёт с сайта."
      : "Удалить комментарий безвозвратно?";
    if (!window.confirm(label)) return;
    setBusyId(c.id);
    setError(null);
    try {
      await deleteArticleComment(c.id);
      if (editingId === c.id) cancelEdit();
      await reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onAddArticle = async () => {
    const slug = newSlug.trim();
    if (!slug) {
      setError("Укажите slug (путь статьи без домена)");
      return;
    }
    setSaveBusy(true);
    setError(null);
    try {
      const r = await upsertAdminArticle({
        slug,
        url: newUrl.trim() || undefined,
        title: newTitle.trim() || undefined,
        markdown: newMarkdown.trim() || undefined,
        fetch_from_url: fetchFromUrl,
      });
      setRagSaveInfo(
        `RAG: добавлено ${r.rag_chunks_added ?? 0}, удалено старых ${r.rag_chunks_removed ?? 0}` +
          (r.rag_warning ? ` · ${r.rag_warning}` : "")
      );
      setNewSlug("");
      setNewUrl("");
      setNewTitle("");
      setNewMarkdown("");
      await reloadAll();
      setSelectedSlug(slug.replace(/^\/+|\/+$/g, ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaveBusy(false);
    }
  };

  const onGenerateComments = async () => {
    if (!selected) return;
    const n = parseInt(genCount, 10);
    if (Number.isNaN(n) || n < 1 || n > 20) {
      setError("Количество комментариев: от 1 до 20");
      return;
    }
    setGenBusy(true);
    setError(null);
    setGenResult(null);
    try {
      const r = await generateArticleComments(selected.slug, n);
      setGenResult(r);
      await reloadAll();
      setCommentStatus("pending");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenBusy(false);
    }
  };

  const onSaveArticle = async () => {
    if (!selected) return;
    const views = parseInt(editViews, 10);
    const likes = parseInt(editLikes, 10);
    if (Number.isNaN(views) || views < 0 || Number.isNaN(likes) || likes < 0) {
      setError("Просмотры и лайки — неотрицательные числа");
      return;
    }
    setSaveBusy(true);
    setError(null);
    try {
      await patchAdminArticle(selected.slug, {
        views_count: views,
        likes_count: likes,
        social_enabled: editEnabled,
      });
      await reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <section className="admin-section admin-articles">
      {error ? <p className="admin-error">{error}</p> : null}

      <p className="admin-muted">
        Социальный виджет статей Hugo: просмотры, лайки и комментарии в{" "}
        <strong>feed.sqlite</strong>. На сайте видны только комментарии со статусом{" "}
        <code>approved</code>. Комментарии из ленты чата попадают сразу как{" "}
        <code>approved</code> — их можно скрыть (отклонить), удалить или отредактировать
        ниже.
      </p>

      <div className="admin-articles-layout">
        <aside className="admin-articles-sidebar">
          <h2>Статьи ({articles.length})</h2>
          {loading && articles.length === 0 ? <p>Загрузка…</p> : null}
          <ul className="admin-list compact admin-articles-list">
            {articles.map((a) => (
              <li key={a.slug}>
                <button
                  type="button"
                  className={
                    selectedSlug === a.slug ?
                      "admin-article-item active"
                    : "admin-article-item"
                  }
                  onClick={() => setSelectedSlug(a.slug)}
                >
                  <span className="admin-article-title">
                    {a.title || a.slug}
                  </span>
                  <span className="admin-article-stats">
                    👁 {a.views_count} · ♥ {a.likes_count}
                    {a.comments_pending > 0 ?
                      ` · ⏳ ${a.comments_pending}`
                    : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="admin-articles-add">
            <h3>Добавить статью</h3>
            <label>
              slug
              <input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="markirovka-tovarov/..."
              />
            </label>
            <label>
              URL (необяз.)
              <input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://www.cleverence.ru/articles/..."
              />
            </label>
            <label>
              Заголовок
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </label>
            <label className="admin-queue-option">
              <input
                type="checkbox"
                checked={fetchFromUrl}
                onChange={(e) => setFetchFromUrl(e.target.checked)}
              />
              <span>Загрузить текст с URL в RAG (если нет markdown ниже)</span>
            </label>
            <label>
              Markdown (необяз., приоритет над URL)
              <textarea
                className="admin-import-textarea"
                rows={4}
                value={newMarkdown}
                onChange={(e) => setNewMarkdown(e.target.value)}
                placeholder="---&#10;title: ...&#10;url: /раздел/статья/&#10;---&#10;Текст статьи..."
              />
            </label>
            <p className="admin-muted">
              При сохранении статья сначала попадает в <code>rag_chunks.jsonl</code>, затем в
              социальную БД.
            </p>
            {ragSaveInfo ? <p className="admin-muted">{ragSaveInfo}</p> : null}
            <button
              type="button"
              className="admin-btn"
              disabled={saveBusy}
              onClick={onAddArticle}
            >
              Сохранить
            </button>
          </div>
        </aside>

        <div className="admin-articles-main">
          {selected ? (
            <div className="admin-article-detail">
              <h2>{selected.title || selected.slug}</h2>
              <p className="admin-muted">
                <a href={selected.url} target="_blank" rel="noreferrer">
                  {selected.url}
                </a>
              </p>
              <div className="admin-exclude-form">
                <label>
                  Просмотры
                  <input
                    value={editViews}
                    onChange={(e) => setEditViews(e.target.value)}
                  />
                </label>
                <label>
                  Лайки
                  <input
                    value={editLikes}
                    onChange={(e) => setEditLikes(e.target.value)}
                  />
                </label>
                <label className="admin-queue-option">
                  <input
                    type="checkbox"
                    checked={editEnabled}
                    onChange={(e) => setEditEnabled(e.target.checked)}
                  />
                  <span>Виджет включён</span>
                </label>
                <button
                  type="button"
                  className="admin-btn approve"
                  disabled={saveBusy}
                  onClick={onSaveArticle}
                >
                  {saveBusy ? "Сохраняем…" : "Сохранить счётчики"}
                </button>
              </div>
              <p className="admin-muted">
                Одобрено: {selected.comments_approved} · в очереди:{" "}
                {selected.comments_pending}
              </p>
              <div className="admin-articles-generate">
                <h3>Генерация из чата + RAG</h3>
                <p className="admin-muted">
                  Подбирает похожие вопросы из архива Telegram, переформулирует и
                  отвечает по тексту статьи. Новые комментарии — в очередь{" "}
                  <code>pending</code>.
                </p>
                <div className="admin-exclude-form">
                  <label>
                    Сколько комментариев
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={genCount}
                      onChange={(e) => setGenCount(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-btn approve"
                    disabled={genBusy}
                    onClick={onGenerateComments}
                  >
                    {genBusy ? "Генерируем…" : "Сгенерировать"}
                  </button>
                </div>
                {genResult ? (
                  <pre className="admin-import-result">
                    {JSON.stringify(genResult, null, 2)}
                  </pre>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="admin-muted">Выберите статью слева или модерируйте комментарии ниже.</p>
          )}

          <div className="admin-articles-comments">
            <div className="admin-articles-comments-head">
              <h2>
                Комментарии
                {commentStatus === "pending" && pendingCount > 0 ?
                  ` (${pendingCount} в очереди)`
                : ""}
              </h2>
              <label>
                Статус{" "}
                <select
                  value={commentStatus}
                  onChange={(e) => setCommentStatus(e.target.value)}
                >
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                  <option value="">все</option>
                </select>
              </label>
              <label className="admin-queue-option">
                <input
                  type="checkbox"
                  checked={filterByArticle}
                  onChange={(e) => setFilterByArticle(e.target.checked)}
                  disabled={!selectedSlug}
                />
                <span>только выбранная статья</span>
              </label>
              <button type="button" className="admin-btn" onClick={() => reloadAll()}>
                Обновить
              </button>
            </div>

            {!loading && comments.length === 0 ? (
              <p className="admin-muted">Нет комментариев с таким фильтром.</p>
            ) : null}

            <ul className="admin-list">
              {comments.map((c) => (
                <li key={c.id} className="admin-card">
                  <div className="admin-meta">
                    #{c.id} · {c.status} · {c.created_at}
                    {c.reviewed_at ? ` · проверено ${c.reviewed_at}` : ""}
                  </div>
                  <div className="admin-meta">
                    <a
                      href={`https://www.cleverence.ru/articles/${c.article_slug}/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {c.article_slug}
                    </a>
                    {" · "}
                    {c.author_display} ({c.kind})
                  </div>
                  {editingId === c.id ? (
                    <div className="admin-comment-edit">
                      <label>
                        Автор
                        <input
                          value={editAuthor}
                          onChange={(e) => setEditAuthor(e.target.value)}
                        />
                      </label>
                      <label>
                        Вопрос
                        <textarea
                          className="admin-import-textarea"
                          rows={2}
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                        />
                      </label>
                      <label>
                        Ответ
                        <textarea
                          className="admin-import-textarea"
                          rows={4}
                          value={editAnswer}
                          onChange={(e) => setEditAnswer(e.target.value)}
                        />
                      </label>
                      <div className="admin-actions">
                        <button
                          type="button"
                          className="admin-btn approve"
                          disabled={busyId === c.id}
                          onClick={() => onSaveComment(c.id)}
                        >
                          {busyId === c.id ? "Сохраняем…" : "Сохранить"}
                        </button>
                        <button
                          type="button"
                          className="admin-btn"
                          disabled={busyId === c.id}
                          onClick={cancelEdit}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {c.question ? (
                        <div className="admin-block">
                          <strong>Вопрос</strong>
                          <p>{c.question}</p>
                        </div>
                      ) : null}
                      <div className="admin-block">
                        <strong>Ответ</strong>
                        <p>{c.answer}</p>
                      </div>
                    </>
                  )}
                  {editingId !== c.id ? (
                    <div className="admin-actions">
                      {c.status === "pending" ? (
                        <>
                          <button
                            type="button"
                            className="admin-btn approve"
                            disabled={busyId === c.id}
                            onClick={() => onApprove(c.id)}
                          >
                            Одобрить
                          </button>
                          <button
                            type="button"
                            className="admin-btn reject"
                            disabled={busyId === c.id}
                            onClick={() => onReject(c.id)}
                          >
                            Отклонить
                          </button>
                        </>
                      ) : c.status === "rejected" ? (
                        <button
                          type="button"
                          className="admin-btn approve"
                          disabled={busyId === c.id}
                          onClick={() => onApprove(c.id)}
                        >
                          Одобрить снова
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="admin-btn reject"
                          disabled={busyId === c.id}
                          onClick={() => onReject(c.id)}
                        >
                          Скрыть с сайта
                        </button>
                      )}
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={busyId === c.id}
                        onClick={() => startEdit(c)}
                      >
                        Редактировать
                      </button>
                      <button
                        type="button"
                        className="admin-btn reject"
                        disabled={busyId === c.id}
                        onClick={() => onDelete(c)}
                      >
                        Удалить
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
